import { useState } from 'react';
import { Link } from 'react-router';
import { EmptyState, ErrorState, Spinner } from '../../components/ui.jsx';
import { LeaseDocument, LeaseStatus, SignLeaseModal } from '../../components/lease.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { useMeta } from '../../state/MetaContext.jsx';
import { formatDate, formatXaf, monthsBetween, rentPeriodMonths, rentPeriodNoun } from '../../lib/format.js';

const MyLease = () => {
  useTitle('My lease');
  const { user } = useAuth();
  const { platform } = useMeta();
  const { data, loading, error, reload } = useFetch('/tenancies?status=any');
  const [signing, setSigning] = useState(null);

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const leases = data.items || [];
  const toSign = leases.filter((lease) => lease.status === 'awaiting_signatures' && !lease.signatures?.tenant);
  const live = leases.filter((lease) => lease.status === 'active');
  const past = leases.filter((lease) => ['ended', 'terminated'].includes(lease.status));
  const shown = [...toSign, ...live, ...leases.filter((lease) => lease.status === 'awaiting_signatures' && lease.signatures?.tenant), ...past];

  if (leases.length === 0) {
    return (
      <div className="container section">
        <EmptyState
          title="You have no lease on the platform"
          message="When your landlord draws up an agreement it appears here for you to read and sign. Nothing binds you until you have signed it yourself."
          action={
            <Link to="/search" className="btn btn-primary">
              Browse houses to rent
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>My lease</h1>
          <p className="muted">Your tenancy agreement, exactly as your landlord signed it.</p>
        </div>
        {live.length > 0 ? (
          <Link to="/tenant/rent" className="btn btn-primary">
            Rent and receipts
          </Link>
        ) : null}
      </header>

      {toSign.length > 0 ? (
        <div className="notice notice-warn">
          <strong>
            {toSign.length === 1 ? 'An agreement is waiting for your signature.' : `${toSign.length} agreements are waiting for your signature.`}
          </strong>{' '}
          Read the whole thing before you sign. Ask your landlord about anything you are unsure of: once you sign, it
          binds you.
        </div>
      ) : null}

      {shown.map((lease) => {
        const leaseMonths = lease.endDate ? monthsBetween(lease.startDate, lease.endDate) : null;
        const leaseTotal = leaseMonths
          ? Math.round((lease.rent / rentPeriodMonths(lease.billingPeriod)) * leaseMonths)
          : null;
        return (
          <section key={lease.id} className="lease-panel">
            <header className="lease-panel-head">
              <div>
                <h2>
                  {lease.unit?.label ? `${lease.unit.label}, ` : ''}
                  {lease.property?.title}
                </h2>
                <p className="muted">
                  {[lease.property?.neighbourhood, lease.property?.city].filter(Boolean).join(', ')} -{' '}
                  {formatXaf(lease.rent)} a {rentPeriodNoun(lease.billingPeriod)} from {formatDate(lease.startDate)}
                  {lease.endDate
                    ? ` to ${formatDate(lease.endDate)} (${leaseMonths} month${leaseMonths === 1 ? '' : 's'}, total ${formatXaf(leaseTotal)})`
                    : ''}
                </p>
              </div>
            <div className="row-actions">
              <LeaseStatus status={lease.status} />
              <Link to={`/leases/${lease.id}/print`} className="btn btn-ghost btn-sm">
                Print or save
              </Link>
            </div>
          </header>

          {lease.status === 'awaiting_signatures' && !lease.signatures?.tenant ? (
            <div className="sign-callout">
              <div>
                <strong>Your landlord has signed.</strong>
                <p>
                  Sign to bring the tenancy into force. You will have to pay for one year.
                  {lease.deposit > 0 ? ` The deposit of ${formatXaf(lease.deposit)} will then appear on your rent statement.` : ''}
                  {leaseMonths ? ` This lease runs for ${leaseMonths} month${leaseMonths === 1 ? '' : 's'} and totals ${formatXaf(leaseTotal)} in rent.` : ''}
                </p>
              </div>
              <button type="button" className="btn btn-primary btn-lg" onClick={() => setSigning(lease)}>
                Read and sign
              </button>
            </div>
          ) : null}

          {lease.status === 'awaiting_signatures' && lease.signatures?.tenant ? (
            <div className="notice notice-info">
              You have signed. Waiting for your landlord to sign as well.
            </div>
          ) : null}

          <LeaseDocument tenancy={lease} platformName={platform?.name} />
        </section>
      )})}

      {signing ? (
        <SignLeaseModal
          tenancy={signing}
          defaultName={user.name}
          onClose={() => setSigning(null)}
          onSigned={() => {
            setSigning(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
};

export default MyLease;
